import type {
    AppUser,
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    DataSourceAbstract,
    DateField,
    Field,
    FieldADTValue,
    FieldCellValue,
    FilterCommonCondition,
    FilterConditions,
    FilterFieldADTValue,
    FilterFormType,
    FilterGroupType,
    NullFieldValue,
    PageVariableSchema,
    RecordLikeProtocol,
    RichTextContentProtocol,
    TypeInstanceMap,
    VariableADTvalue
} from '@lighthouse/core'
import { DateFieldUnit, DEVICE_ID_VALUE, FilterStatus, ImageVariableValue, VariableType } from '@lighthouse/core'
import { arrayContainsArray, getKeyByValue } from '@lighthouse/tools'
import {
    addDays,
    addWeeks,
    endOfDay,
    endOfMinute,
    endOfMonth,
    endOfYear,
    getDay,
    isSameDay,
    isSameMinute,
    isSameMonth,
    isSameYear,
    lightFormat,
    previousDay,
    startOfDay,
    startOfMinute,
    startOfMonth,
    startOfYear,
    subDays,
    subMonths
} from 'date-fns'
import equal from 'fast-deep-equal'
import { clone, find, isEmpty, max } from 'rambda'
import type { MakeADT } from 'ts-adt/MakeADT'

import { imageVariableNameMap, isEmptyRichTextValue } from '../components'
import {
    defaultDateFormat,
    defaultFormat,
    FilterWayToInnerTypeMap,
    innerTypeToFieldType,
    USER_DATASOURCE,
    variableSystemFormatMap,
    variableSystemNameMap,
    visibleFilterOperatorText
} from '../constants'
import type { DeptOption, RoleOption, UserOption } from '../types'
import { isDateValue, isNumberValue } from './helper'
import { fromShanghaiWall, isBeijingTimeZone, toShanghaiWall } from './shanghaiTime'
// id 为记录的ID字段的值 或 用户id
export type CustomViewVisibleData = {
    name: string
    dataSource?: DataSourceAbstract
    extraFields?: Field[]
}
type TransformFieldValue = FilterFieldADTValue

const filterNotSupport: Record<string, string[]> = {
    'array-text': ['>', '<', '>=', '<=', 'startWith', 'endWith'],
    'array-date': ['=', '!=', '>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'array-bool': ['>', '<', '>=', '<=', 'startWith', 'endWith'],
    'array-number': ['>', '<', '>=', '<=', 'startWith', 'endWith'],
    'array-null': ['>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'boolean-date': ['=', '!=', '>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'boolean-boolean': ['startWith', 'endWith', 'contains', 'notContains'],
    'boolean-null': ['>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'date-date': ['startWith', 'endWith', 'contains', 'notContains'],
    'date-text': ['=', '!=', '>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'date-null': ['>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'date-number': ['=', '!=', '>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains'],
    'text-text': ['>', '<', '>=', '<='],
    'number-number': ['startWith', 'endWith', 'contains', 'notContains'],
    'number-null': ['=', '!=', '>', '<', '>=', '<=', 'startWith', 'endWith', 'contains', 'notContains']
}

// 数组 >  文本 > 布尔 > 数值 > 日期
// type DataType = 'array' | 'text' | 'boolean' | 'number' | 'null'
const dataLevel: Record<TypeInstanceMap, number> = {
    ARRAY: 5,
    TEXT: 4,
    BOOL: 3,
    NUMBER: 2,
    DATE: 1,
    NULL: 0,
    RUNTIME: 0,
    RICH_TEXT: 6
    // date: 1
}

type dataADTValue = MakeADT<
    'innerType',
    {
        TEXT: { value: string; param: string }
        ARRAY: { value: string[]; param: string[] }
        NUMBER: { value: number; param: number }
        BOOLEAN: { value: boolean; param: boolean }
    }
>

const getDataLevel = (innerType: TypeInstanceMap | undefined): number => {
    return dataLevel[innerType || 'NULL']
}

const getMaxDataLevel = (valueInnerType: TypeInstanceMap, paramInnerType: TypeInstanceMap | undefined) => {
    const valueLevel = getDataLevel(valueInnerType)
    const paramLevel = getDataLevel(paramInnerType)
    const maxLevel = max(valueLevel, paramLevel)
    return {
        valueLevel,
        paramLevel,
        maxLevel
    }
}

const upgradeDataLevel = (filterValue: FilterFieldADTValue | undefined, targetLevel: number) => {
    const value = filterValue?.value
    switch (targetLevel) {
        case 5: {
            if (value === undefined || value === null) {
                return []
            }
            return String(value).split(',')
        }
        case 4: {
            if (filterValue?.innerType === 'DATE' && typeof value === 'number') {
                const format = filterValue.type === 'date' ? filterValue.date.format || defaultFormat : defaultFormat
                return lightFormat(value, format)
            }
            if (value === undefined || value === null) {
                return ''
            }
            return String(value).trim()
        }
        case 3: {
            return Boolean(value)
        }
        case 2: {
            if (value === undefined || value === '' || value === null || Array.isArray(value)) {
                return Number.NaN
            }
            return Number(value)
        }
        default: {
            return undefined
        }
    }
}

const upgradeSameLevel = (valueValue: TransformFieldValue, paramValue?: TransformFieldValue) => {
    if (valueValue.innerType === 'RUNTIME' && paramValue?.innerType === 'RUNTIME') {
        const data = {
            innerType: 'TEXT',
            value: valueValue.value,
            param: paramValue?.value
        } as dataADTValue
        return { data, level: 4 }
    }
    const { valueLevel, paramLevel } = getMaxDataLevel(valueValue.innerType, paramValue?.innerType)
    const maxLevel = max(valueLevel, paramLevel)
    const innerType = getKeyByValue(maxLevel, dataLevel)
    const data = {
        innerType,
        value: upgradeDataLevel(valueValue, maxLevel),
        param: upgradeDataLevel(paramValue, maxLevel)
    } as dataADTValue
    return {
        data,
        level: maxLevel
    }
}

const getCompareWay = (valueInnerType: TypeInstanceMap, paramInnerType: TypeInstanceMap) => {
    if (valueInnerType === 'RUNTIME' && paramInnerType === 'RUNTIME') {
        return 'TEXT-TEXT'
    }

    const compareWayList = [valueInnerType, paramInnerType].sort()
    return compareWayList.join('-')
}

const getIsSupport = (valueInnerType: TypeInstanceMap, paramInnerType: TypeInstanceMap, operator: string) => {
    if (operator === 'isEmpty' || operator === 'isNotEmpty') {
        return true
    }
    const compareWay = getCompareWay(valueInnerType, paramInnerType)
    const notSupportOperators: string[] | undefined = filterNotSupport[compareWay]
    return !notSupportOperators || !notSupportOperators.includes(operator)
}

export const getDateUnitByFormat = (format: string) => {
    if (format?.includes('dd') && format?.includes('mm')) {
        return { unit: DateFieldUnit.DAY_MINUTE, format }
    }
    if (format?.includes('mm')) {
        return { unit: DateFieldUnit.MINUTE, format }
    }
    if (format?.includes('dd')) {
        return { unit: DateFieldUnit.DAY, format }
    }
    if (format?.includes('MM')) {
        return { unit: DateFieldUnit.MONTH, format }
    }
    return { unit: DateFieldUnit.YEAR, format }
}

export const getDateConfig = (field: Field | DateField | undefined) => {
    if (field?.type === 'date') {
        const format = field.date?.format || defaultDateFormat
        return getDateUnitByFormat(format)
    }
    return { unit: DateFieldUnit.MINUTE, format: defaultDateFormat }
}

const booleanToFilterStatus = (bool: boolean) => {
    if (bool) {
        return FilterStatus.pass
    }
    return FilterStatus.noPass
}

type GetDateValuePayload = {
    firstParam: FieldCellValue
    secondParam?: FieldCellValue
}

export const getDateValue = ({ firstParam, secondParam }: GetDateValuePayload, now = Date.now()): number | [number, number] => {
    const today = now
    switch (firstParam) {
        case 'AFTER_SOMEWHAT_DAYS': {
            if (!Number.isNaN(Number(secondParam))) {
                const first = addDays(today, Number(secondParam)).valueOf()
                return [startOfDay(first).valueOf(), Infinity]
            }
            return Number.NaN
        }
        case 'BEFORE_SOMEWHAT_DAYS': {
            if (!Number.isNaN(Number(secondParam))) {
                const first = subDays(today, Number(secondParam)).valueOf()
                return [0, first]
            }
            return Number.NaN
        }
        case 'NOW': {
            return [today, today]
        }
        case 'TODAY': {
            return [startOfDay(today).valueOf(), endOfDay(today).valueOf()]
        }
        case 'TOMORROW': {
            const date = addDays(today, 1).valueOf()
            return [startOfDay(date).valueOf(), endOfDay(date).valueOf()]
        }
        case 'YESTERDAY': {
            const date = subDays(today, 1).valueOf()
            return [startOfDay(date).valueOf(), endOfDay(date).valueOf()]
        }
        case 'THIS_WEEK': {
            // nextDay
            const day = getDay(today)
            const weekStartDay = startOfDay(previousDay(today, -day + 1)).valueOf()
            const weekEndDay = endOfDay(addDays(today, 7 - day)).valueOf()
            return [weekStartDay, weekEndDay]
        }
        case 'LAST_WEEK': {
            const day = getDay(today)
            const prevDay = addWeeks(today, -1)
            const weekStartDay = startOfDay(previousDay(prevDay, -day + 1)).valueOf()
            const weekEndDay = endOfDay(addDays(prevDay, 7 - day)).valueOf()
            return [weekStartDay, weekEndDay]
        }
        case 'THIS_MONTH': {
            const start = startOfMonth(today).valueOf()
            const end = endOfMonth(today).valueOf()
            return [start, end]
        }
        case 'LAST_MONTH': {
            const prevMonthDay = subMonths(today, 1)
            const start = startOfMonth(prevMonthDay).valueOf()
            const end = endOfMonth(prevMonthDay).valueOf()
            return [start, end]
        }
        case 'THIS_YEAR': {
            const start = startOfYear(today).valueOf()

            const end = endOfYear(today).valueOf()
            return [start, end]
        }
        case 'PAST_SEVEN_DAYS': {
            const start = startOfDay(subDays(today, 7)).valueOf()
            // const end = endOfDay(subDays(today, 7)).valueOf()
            return [start, endOfDay(today).valueOf()]
        }
        case 'FUTURE_SEVEN_HEAVENS': {
            // const start = startOfDay(addDays(today, 7)).valueOf()
            const end = endOfDay(addDays(today, 7)).valueOf()
            return [startOfDay(today).valueOf(), end]
        }
        case 'PAST_THIRTY_DAYS': {
            const start = startOfDay(subDays(today, 30)).valueOf()
            // const end = endOfDay(subDays(today, 30)).valueOf()
            return [start, endOfDay(today).valueOf()]
        }
        case 'FUTURE_THIRTY_HEAVENS': {
            // const start = startOfDay(addDays(today, 30)).valueOf()
            const end = endOfDay(addDays(today, 30)).valueOf()
            return [startOfDay(today).valueOf(), end]
        }

        default: {
            return isNumberValue(firstParam) ? Number(firstParam) : Number.NaN
        }
    }
}

function wallBoundToInstant(bound: number) {
    if (bound === 0 || !Number.isFinite(bound)) {
        return bound
    }
    return fromShanghaiWall(new Date(bound))
}

/** 今天、本周这类语义日期。北京时区直接在北京日历上取边界。固定时间戳不要走这里。 */
export function getZonedDateValue(payload: GetDateValuePayload, timeZone?: string, now = Date.now()) {
    if (!isBeijingTimeZone(timeZone)) {
        return getDateValue(payload, now)
    }
    const zoned = getDateValue(payload, toShanghaiWall(now).getTime())
    if (typeof zoned === 'number') {
        return wallBoundToInstant(zoned)
    }
    return [wallBoundToInstant(zoned[0]), wallBoundToInstant(zoned[1])] as [number, number]
}

export const verifyFieldValueEmpty = (fieldValue: TransformFieldValue) => {
    if (fieldValue.innerType === 'ARRAY') {
        return !fieldValue.value || isEmpty(fieldValue.value)
    }
    if (fieldValue.innerType === 'TEXT') {
        return !fieldValue.value || (typeof fieldValue.value === 'string' && !fieldValue.value?.trim())
    }
    if (fieldValue.innerType === 'NUMBER') {
        return (typeof fieldValue.value === 'string' && !fieldValue.value?.trim()) || Number.isNaN(Number(fieldValue.value))
    }
    if (fieldValue.innerType === 'NULL') {
        return true
    }
    if (fieldValue.innerType === 'RICH_TEXT') {
        const doc = fieldValue.value as RichTextContentProtocol
        return isEmptyRichTextValue(doc)
    }
    return !fieldValue.value
}

const verifyFilter = (value: TransformFieldValue, operator: string, param?: TransformFieldValue) => {
    if (operator === 'isEmpty') {
        return booleanToFilterStatus(verifyFieldValueEmpty(value))
    }

    if (operator === 'isNotEmpty') {
        return booleanToFilterStatus(!verifyFieldValueEmpty(value))
    }

    const { data, level } = upgradeSameLevel(value, param)

    // eslint-disable-next-line no-constant-binary-expression
    if (data.value === undefined || !data.param === undefined) {
        return FilterStatus.noPass
    }
    switch (operator) {
        case '=': {
            if (data.innerType === 'ARRAY') {
                return booleanToFilterStatus(equal(data.value.sort(), data.param.sort()))
            }
            return booleanToFilterStatus(data.value === data.param)
        }
        case '!=': {
            if (data.innerType === 'ARRAY') {
                return booleanToFilterStatus(!equal(data.value.sort(), data.param.sort()))
            }
            return booleanToFilterStatus(data.value !== data.param)
        }
        case '>': {
            if (data.innerType === 'NUMBER') {
                return booleanToFilterStatus(data.value > data.param)
            }
            return FilterStatus.noPass
        }
        case '>=': {
            if (data.innerType === 'NUMBER') {
                return booleanToFilterStatus(data.value >= data.param)
            }
            return FilterStatus.noPass
        }
        case '<': {
            if (data.innerType === 'NUMBER') {
                return booleanToFilterStatus(data.value < data.param)
            }
            return FilterStatus.noPass
        }
        case '<=': {
            if (data.innerType === 'NUMBER') {
                return booleanToFilterStatus(data.value <= data.param)
            }
            return FilterStatus.noPass
        }

        case 'contains': {
            if (data.innerType === 'ARRAY') {
                return booleanToFilterStatus(arrayContainsArray(data.value, data.param))
            }
            if (data.innerType === 'TEXT') {
                return booleanToFilterStatus(data.value.includes(data.param))
            }
            return FilterStatus.noPass
        }
        case 'notContains': {
            if (data.innerType === 'ARRAY') {
                return booleanToFilterStatus(!arrayContainsArray(data.value, data.param))
            }
            if (data.innerType === 'TEXT') {
                return booleanToFilterStatus(!data.value.includes(data.param))
            }
            return FilterStatus.noPass
        }
        case 'startWith': {
            if (data.innerType === 'TEXT') {
                return booleanToFilterStatus(!data.value.startsWith(data.param))
            }
            return FilterStatus.noPass
        }
        case 'endWith': {
            if (data.innerType === 'TEXT') {
                return booleanToFilterStatus(!data.value.endsWith(data.param))
            }
            return FilterStatus.noPass
        }

        default: {
            return FilterStatus.init
        }
    }
}

const getRangeDateValueByUnit = (value: [number, number], unit: DateFieldUnit) => {
    if (unit === DateFieldUnit.YEAR) {
        const start = startOfYear(value[0]).valueOf()
        const end = value[1] === Infinity ? value[1] : endOfYear(value[1]).valueOf()
        return {
            start,
            end
        }
    }
    if (unit === DateFieldUnit.MONTH) {
        const start = startOfMonth(value[0]).valueOf()
        const end = value[1] === Infinity ? value[1] : endOfMonth(value[1]).valueOf()
        return {
            start,
            end
        }
    }
    if (unit === DateFieldUnit.DAY) {
        const start = startOfDay(value[0]).valueOf()
        const end = value[1] === Infinity ? value[1] : endOfDay(value[1]).valueOf()
        return {
            start,
            end
        }
    }

    const start = startOfMinute(value[0]).valueOf()
    const end = value[1] === Infinity ? value[1] : endOfMinute(value[1]).valueOf()
    return {
        start,
        end
    }
}

const getDateValueByUnit = (value: number, unit: DateFieldUnit) => {
    if (unit === DateFieldUnit.YEAR) {
        const start = startOfYear(value).valueOf()
        const end = endOfYear(value).valueOf()
        return {
            start,
            end
        }
    }
    if (unit === DateFieldUnit.MONTH) {
        const start = startOfMonth(value).valueOf()
        const end = endOfMonth(value).valueOf()
        return {
            start,
            end
        }
    }

    if (unit === DateFieldUnit.DAY) {
        const start = startOfDay(value).valueOf()
        const end = endOfDay(value).valueOf()
        return {
            start,
            end
        }
    }
    const start = startOfMinute(value).valueOf()
    const end = endOfMinute(value).valueOf()
    return {
        start,
        end
    }
}

const getFormat = (leftFormat: string | undefined, firstParam: TransformFieldValue) => {
    const rightFormat = firstParam.type === 'date' ? firstParam.date?.format : undefined
    const rightIsSystem = firstParam.type === 'date' ? !!firstParam.date?.isSystem : false
    const firstParamInnerType = firstParam.innerType
    if (leftFormat && rightFormat) {
        if (rightIsSystem) {
            return rightFormat
        }
        if (firstParamInnerType) {
            return leftFormat
        }
        return leftFormat.length > rightFormat.length ? leftFormat : rightFormat
    }
    return leftFormat || rightFormat || defaultDateFormat
}

const verifyDateFilter = (dateFieldValue: TransformFieldValue, operator: string, paramList: TransformFieldValue[]) => {
    const v = dateFieldValue.value
    if (operator === 'isEmpty') {
        const isEmptyArray = Array.isArray(v) ? v.length === 0 : false
        return booleanToFilterStatus((!v && v !== 0) || isEmptyArray)
    }

    if (operator === 'isNotEmpty') {
        const isEmptyArray = Array.isArray(v) ? v.length === 0 : false
        return booleanToFilterStatus((!!v || v === 0) && !isEmptyArray)
    }

    const firstParam = paramList?.[0]
    const secondParam = paramList?.[1]
    if (dateFieldValue.type === 'null' && firstParam.type === 'null') {
        return FilterStatus.pass
    }
    const { value } = dateFieldValue
    const dateValue = value && isDateValue(value) ? value : undefined
    if (!dateValue) {
        return FilterStatus.noPass
    }
    // eslint-disable-next-line unicorn/consistent-destructuring
    const leftFormat = dateFieldValue.type === 'date' ? dateFieldValue.date?.format : undefined

    const format = getFormat(leftFormat, firstParam)

    const { unit } = getDateUnitByFormat(format)
    const fieldValue = getDateValue({ firstParam: dateValue })
    const paramValue = getDateValue({
        firstParam: firstParam.value as FieldCellValue,
        secondParam: secondParam?.value as FieldCellValue
    })
    if (!paramValue && operator !== 'isEmpty' && operator !== 'isNotEmpty') {
        return FilterStatus.noPass
    }

    if (Array.isArray(fieldValue) && Array.isArray(paramValue)) {
        return FilterStatus.noPass
    }
    switch (operator) {
        case '=': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    const { start, end } = getRangeDateValueByUnit(fieldValue, unit)
                    const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                    return booleanToFilterStatus(paramStart === start && end === paramEnd)
                }
                const { start, end } = getRangeDateValueByUnit(fieldValue, unit)
                const { start: paramStart, end: paramEnd } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(paramStart <= start && end <= paramEnd)
            }
            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                const { start, end } = getDateValueByUnit(fieldValue, unit)
                return booleanToFilterStatus(paramStart <= start && end <= paramEnd)
            }
            if (unit === DateFieldUnit.YEAR) {
                return booleanToFilterStatus(isSameYear(Number(value), Number(paramValue)))
            }
            if (unit === DateFieldUnit.MONTH) {
                return booleanToFilterStatus(isSameMonth(Number(value), Number(paramValue)))
            }
            if (unit === DateFieldUnit.DAY) {
                return booleanToFilterStatus(isSameDay(Number(value), Number(paramValue)))
            }

            return booleanToFilterStatus(isSameMinute(Number(value), Number(paramValue)))
        }
        case '!=': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    const { start, end } = getRangeDateValueByUnit(fieldValue, unit)
                    const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                    return booleanToFilterStatus(paramStart !== start || end !== paramEnd)
                }
                const { start, end } = getRangeDateValueByUnit(fieldValue, unit)
                const { start: paramStart, end: paramEnd } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(paramStart > start || end > paramEnd)
            }
            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                const { start, end } = getDateValueByUnit(fieldValue, unit)
                return booleanToFilterStatus(paramStart > start || end > paramEnd)
            }
            if (unit === DateFieldUnit.YEAR) {
                return booleanToFilterStatus(!isSameYear(Number(value), Number(paramValue)))
            }
            if (unit === DateFieldUnit.MONTH) {
                return booleanToFilterStatus(!isSameMonth(Number(value), Number(paramValue)))
            }
            if (unit === DateFieldUnit.DAY) {
                return booleanToFilterStatus(!isSameDay(Number(value), Number(paramValue)))
            }

            return booleanToFilterStatus(!isSameMinute(Number(value), Number(paramValue)))
        }
        // case 'between':
        // case 'contains': {
        //     if (Array.isArray(dateValue)) {
        //         if (unit === DateFieldUnit.MONTH) {
        //             const start = startOfMonth(numValue).valueOf()
        //             const end = endOfMonth(numValue).valueOf()
        //             return booleanToFilterStatus(dateValue[0] <= start && end <= dateValue[1])
        //         }
        //         return booleanToFilterStatus(dateValue[0] <= numValue && numValue <= dateValue[1])
        //     }
        //     if (unit === DateFieldUnit.MONTH) {
        //         const start = startOfMonth(numValue).valueOf()
        //         const end = endOfMonth(numValue).valueOf()
        //         return booleanToFilterStatus(start <= Number(dateValue) && Number(dateValue) <= end)
        //     }
        //     if (unit === DateFieldUnit.MINUTE) {
        //         const start = startOfMinute(numValue).valueOf()
        //         const end = endOfMinute(numValue).valueOf()
        //         return booleanToFilterStatus(start <= Number(dateValue) && Number(dateValue) <= end)
        //     }
        //     if (unit === DateFieldUnit.DAY) {
        //         const start = startOfDay(numValue).valueOf()
        //         const end = endOfDay(numValue).valueOf()
        //         return booleanToFilterStatus(start <= Number(dateValue) && Number(dateValue) <= end)
        //     }
        //     return booleanToFilterStatus(isSameDay(Number(value), Number(dateValue)))
        // }
        case '<=': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    return FilterStatus.noPass
                }
                const { end } = getRangeDateValueByUnit(fieldValue, unit)
                const { end: paramEnd } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(end <= paramEnd)
            }

            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start, end } = getDateValueByUnit(fieldValue, unit)
                const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                const resultEnd = paramEnd === Infinity ? getDateValueByUnit(paramStart, unit).end : paramEnd
                return booleanToFilterStatus(end <= resultEnd)
            }
            const { start, end } = getDateValueByUnit(fieldValue, unit)
            const { start: paramStart, end: paramEnd } = getDateValueByUnit(paramValue, unit)

            return booleanToFilterStatus(end <= paramEnd)
        }
        case '>=': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    return FilterStatus.noPass
                }
                const { start } = getRangeDateValueByUnit(fieldValue, unit)
                const { start: paramStart } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(start >= paramStart)
            }

            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start } = getDateValueByUnit(fieldValue, unit)
                const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                const resultStart = paramStart === 0 ? getDateValueByUnit(paramEnd, unit).start : paramStart
                return booleanToFilterStatus(start >= resultStart)
            }
            const { start } = getDateValueByUnit(fieldValue, unit)
            const { start: paramStart } = getDateValueByUnit(paramValue, unit)
            return booleanToFilterStatus(start >= paramStart)
        }
        case '<': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    return FilterStatus.noPass
                }
                const { end } = getRangeDateValueByUnit(fieldValue, unit)
                const { start: paramStart } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(end < paramStart)
            }

            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start, end } = getDateValueByUnit(fieldValue, unit)
                const { start: paramStart, end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(end < paramStart)
            }
            const { start, end } = getDateValueByUnit(fieldValue, unit)
            const { start: paramStart, end: paramEnd } = getDateValueByUnit(paramValue, unit)
            return booleanToFilterStatus(end < paramStart)
        }
        case '>': {
            if (Array.isArray(fieldValue)) {
                if (Array.isArray(paramValue)) {
                    return FilterStatus.noPass
                }
                const { start } = getRangeDateValueByUnit(fieldValue, unit)
                const { end: paramEnd } = getDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(start > paramEnd)
            }

            if (Array.isArray(paramValue)) {
                if (Array.isArray(fieldValue)) {
                    return FilterStatus.noPass
                }
                const { start } = getDateValueByUnit(fieldValue, unit)
                const { end: paramEnd } = getRangeDateValueByUnit(paramValue, unit)
                return booleanToFilterStatus(start > paramEnd)
            }
            const { start } = getDateValueByUnit(fieldValue, unit)
            const { end: paramEnd } = getDateValueByUnit(paramValue, unit)
            return booleanToFilterStatus(start > paramEnd)
        }

        default: {
            return FilterStatus.init
        }
    }
}

type CheckOperatorPayload = {
    fieldValue: TransformFieldValue
    operator: string
    params: TransformFieldValue[]
}

const checkOperator: (payload: CheckOperatorPayload) => FilterStatus = payload => {
    const { fieldValue, operator, params } = payload
    // const isHasParams = isEmptyOperator(operator)

    const firstParamFieldValue = params?.[0]

    // if(fieldValue === undefined){
    //     return FilterStatus.noPass
    // }
    const fieldValueVariable = fieldValue
    const { innerType } = fieldValueVariable
    const isSupport = getIsSupport(innerType, firstParamFieldValue?.innerType || 'RUNTIME', operator)
    if (!isSupport) {
        return FilterStatus.noPass
    }
    const fieldInnerType = innerType === 'RUNTIME' ? firstParamFieldValue.innerType : innerType
    switch (fieldInnerType) {
        case 'TEXT':
        case 'NUMBER':
        case 'BOOL':
        case 'NULL': {
            return verifyFilter(fieldValueVariable, operator, firstParamFieldValue)
        }
        case 'ARRAY': {
            const data = firstParamFieldValue?.value
            if (Array.isArray(data)) {
                return verifyFilter(fieldValueVariable, operator, { type: 'person', value: data, innerType: 'ARRAY' } as FieldADTValue)
            }
            return verifyFilter(fieldValueVariable, operator, firstParamFieldValue)
        }
        case 'DATE': {
            if (!fieldValue) {
                return FilterStatus.noPass
            }
            return verifyDateFilter(fieldValue, operator, params)
        }

        default: {
            return verifyFilter(fieldValueVariable, operator, firstParamFieldValue)
        }
    }
}

const filterPassStatusToBoolean = (filterStatus: FilterStatus) => {
    return filterStatus === FilterStatus.pass
}

type transformParamListPayload = {
    paramList: VariableADTvalue[]
    getVariableToFieldValue: (value: VariableADTvalue) => FilterFieldADTValue | undefined
}

const transformParamList = (payload: transformParamListPayload) => {
    const { paramList, getVariableToFieldValue } = payload
    const result: TransformFieldValue[] = []

    for (const item of paramList) {
        if (item.type === VariableType.FIELD_ID) {
            return FilterStatus.init
        }
        const filterValue = getVariableToFieldValue(item)

        if (filterValue === undefined) {
            return FilterStatus.noPass
        }

        if (filterValue?.isIgnore) {
            return FilterStatus.init
        }
        // const format = filterValue.type === 'date' ? filterValue.date?.format : undefined
        result.push(filterValue)
    }
    return result
}

type FilterRecordUnmatchedPayload = {
    where: 'AND' | 'OR'
    conditions: FilterConditions[]
    initStatus: boolean
    getVariableToFieldValue: (value: VariableADTvalue) => FilterFieldADTValue | undefined
}
const filterRecordUnmatched: (payload: FilterRecordUnmatchedPayload) => FilterStatus = ({
    where,
    conditions,
    initStatus,
    getVariableToFieldValue
}) => {
    if (conditions.length === 0) {
        return FilterStatus.init
    }
    const statusArr = conditions.reduce<FilterStatus[]>((prev, cur) => {
        const childrenConditions = (cur as FilterGroupType)?.conditions
        const childrenWhere = (cur as FilterGroupType)?.where
        if (childrenConditions && childrenWhere) {
            const status = filterRecordUnmatched({
                where: childrenWhere,
                conditions: childrenConditions,
                initStatus,
                getVariableToFieldValue
            })
            if (status !== FilterStatus.init) {
                prev.push(status)
            }
            return prev
        }
        const condition = cur as FilterCommonCondition
        const { idVariable, operator, paramList } = condition
        if (!idVariable || !operator || !paramList) {
            return prev
        }
        const data = transformParamList({
            paramList,
            getVariableToFieldValue
        })

        if (operator !== 'isEmpty' && operator !== 'isNotEmpty') {
            if (data === FilterStatus.init) {
                return prev
            }

            if (data === FilterStatus.noPass) {
                prev.push(data)
                return prev
            }
        }

        const fieldValue = getVariableToFieldValue(idVariable)

        if (fieldValue === undefined) {
            prev.push(FilterStatus.noPass)
            return prev
        }

        if (fieldValue?.isIgnore) {
            // FilterStatus.init
            return prev
        }

        const params = data === FilterStatus.init || data === FilterStatus.noPass ? [] : data

        const verify = checkOperator({
            fieldValue,
            operator,
            params
        })

        if (initStatus && verify !== FilterStatus.init) {
            prev.push(verify)
        }
        if (!initStatus) {
            prev.push(verify)
        }

        return prev
    }, [])

    if (where === 'OR') {
        return booleanToFilterStatus(
            statusArr.reduce((accumulator, currentValue) => accumulator || filterPassStatusToBoolean(currentValue), false)
        )
    }
    if (!initStatus && statusArr.length === 0) {
        return booleanToFilterStatus(false)
    }
    return booleanToFilterStatus(
        statusArr.reduce((accumulator, currentValue) => accumulator && filterPassStatusToBoolean(currentValue), true)
    )
}

type FilterRecordCheckerPayload = {
    filter: FilterFormType
    initStatus?: boolean
    // runtime?: boolean
    userRecord?: {
        datasource?: DataSourceAbstract
        record?: RecordLikeProtocol
    }

    getVariableToFieldValue: (value: VariableADTvalue) => FieldADTValue | undefined
}

const NULL_FIELD_VALUE: NullFieldValue = { id: '', dsId: '', name: 'null', type: 'null', innerType: 'NULL', value: null }

export const filterRecordChecker = (payload: FilterRecordCheckerPayload) => {
    const { filter, initStatus = true, getVariableToFieldValue } = payload

    // 1. 如果是用户变量，当用户未登录时，该条件进行比较时忽略，直接通过校验
    // 2. 如果是VALUE变量, 当值为空时，跳过
    const modify = (value: VariableADTvalue): TransformFieldValue | undefined => {
        if (value.type === VariableType.VALUE) {
            const idVariableValue = value.valueVariable?.value
            const isIgnore =
                idVariableValue === null ||
                idVariableValue === undefined ||
                Number.isNaN(idVariableValue) ||
                (typeof idVariableValue === 'string' && !idVariableValue.trim()) ||
                (Array.isArray(idVariableValue) && idVariableValue.length === 0)

            const f = getVariableToFieldValue(value)
            if (!f) {
                return
            }

            return { ...f, isIgnore }
        }

        if (value.type === VariableType.SYSTEM) {
            return {
                id: '',
                type: 'date',
                value: value.systemVariable?.value,
                date: {
                    format: value.systemVariable?.value ? variableSystemFormatMap[value.systemVariable?.value] : 'yyyy-MM-dd',
                    isSystem: true
                },
                innerType: 'DATE'
            } as FieldADTValue
        }

        const fieldValue = getVariableToFieldValue(value)
        if (!fieldValue) {
            return NULL_FIELD_VALUE
        }
        return fieldValue
    }

    const conditions = filter.expression?.conditions
    const where = filter.expression?.where
    if (conditions && where) {
        const filterStatus = filterRecordUnmatched({
            where,
            conditions,
            initStatus,
            getVariableToFieldValue: modify
        })
        return filterStatus === FilterStatus.init || filterStatus === FilterStatus.pass
    }
    return initStatus
}

type GetDataSourceByIdVariablePayload = {
    formDsId?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    idVariable?: VariableADTvalue
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
}

const getDataSourceByIdVariable = (payload: GetDataSourceByIdVariablePayload) => {
    const { formDsId, customViewData, dataSourceList, idVariable, availableFieldBlocksInfo, pageVariableSchemas } = payload
    if (!idVariable) {
        return
    }
    if (idVariable.type === VariableType.FIELD_ID) {
        return
    }
    if (idVariable.type === VariableType.VALUE && idVariable.valueVariable?.value === '{currentUserId}') {
        return
    }
    if (idVariable.type === VariableType.PAGE_DATA_QUERY) {
        const schema = pageVariableSchemas?.find(item => item.id === idVariable.pageDataQueryVariable?.id)
        return find(item => item.id === schema?.dsId, dataSourceList)
    }
    if (idVariable.type === VariableType.VIEW) {
        return customViewData?.dataSource
    }
    if (idVariable.type === VariableType.USER) {
        return find(item => item.id === USER_DATASOURCE, dataSourceList)
    }
    if (idVariable.type === VariableType.FORM) {
        return find(item => item.id === formDsId, dataSourceList)
    }
    if (idVariable.type === VariableType.INPUT) {
        const fieldBlockWithDsId = find(item => item.block.id === idVariable?.inputVariable?.blockId, availableFieldBlocksInfo)
        if (!fieldBlockWithDsId) {
            return
        }
        if (fieldBlockWithDsId.block.type === 'field') {
            return find(item => item.id === fieldBlockWithDsId.formContainer?.config?.pointer, dataSourceList)
        }
    }
    return undefined
}

const getFieldByIdVariable = (
    dataSource: DataSourceAbstract | undefined,
    idVariable: VariableADTvalue | undefined,
    availableFieldBlocksInfo: AvailableFieldBlockInfo[],
    availableFilterBlocksInfo: AvailableFilterBlockInfo[],
    pageVariableSchemas?: PageVariableSchema[],
    extraViewFields?: Field[]
) => {
    if (!idVariable) {
        return
    }
    if (idVariable.type === VariableType.FIELD_ID) {
        return
    }
    if (idVariable.type === VariableType.VALUE && idVariable.valueVariable?.value === '{currentUserId}') {
        return { id: '', type: 'person', innerType: 'ARRAY' } as Field
    }
    if (idVariable.type === VariableType.PAGE_DATA_QUERY) {
        const schema = pageVariableSchemas?.find(item => item.id === idVariable.pageDataQueryVariable?.id)
        const fieldId = schema?.items.find(item => item.id === idVariable.pageDataQueryVariable?.itemId)?.fieldId || ''
        const field = dataSource?.schema[fieldId]
        if (!field) {
            return
        }
        return field
    }
    if (idVariable.type === VariableType.VIEW) {
        const fieldId = idVariable.viewVariable?.fieldId || ''
        const field = dataSource?.schema[fieldId] ?? extraViewFields?.find(item => item.id === fieldId)
        if (!field) {
            return
        }
        return field
    }
    if (idVariable.type === VariableType.USER) {
        const field = dataSource?.schema[idVariable.userVariable?.fieldId || '']
        if (!field) {
            return
        }
        return field
    }

    if (idVariable.type === VariableType.ROUTE_PATH || idVariable.type === VariableType.ROUTE_QUERY) {
        return { id: '', type: 'text', innerType: 'TEXT' } as Field
    }

    if (idVariable.type === VariableType.INPUT) {
        const fieldBlock = find(item => item.block.id === idVariable.inputVariable?.blockId, availableFieldBlocksInfo)
        if (!fieldBlock) {
            return
        }
        if (fieldBlock.block.type === 'field') {
            const field = dataSource?.schema[fieldBlock.block.config?.fieldPointer || '']
            if (!field) {
                return
            }
            return field
        }
        if (fieldBlock.block.type === 'onOff') {
            return {
                id: fieldBlock.block.id,
                type: 'checkbox',
                innerType: 'BOOL',
                name: fieldBlock.block.title || ''
            } as Field
        }
    }
    if (idVariable.type === VariableType.FILTER) {
        const { blockId, itemId } = idVariable.filterVariable || {}
        const filterBlockInfo = find(item => item.block.id === blockId, availableFilterBlocksInfo)
        const filterItem = find(item => item.id === itemId, filterBlockInfo?.block.config.filterItems || [])
        if (filterItem?.filterWay) {
            const innerType = FilterWayToInnerTypeMap[filterItem.filterWay]
            return {
                id: '',
                type: innerTypeToFieldType[innerType],
                innerType
            } as Field
        }
    }
    if (idVariable.type === VariableType.IMAGE) {
        const { dataKey } = idVariable.imageVariable || {}
        if (!dataKey) {
            return
        }
        if (dataKey === ImageVariableValue.IMAGE_INDEX) {
            return {
                id: '',
                type: 'number',
                innerType: 'NUMBER'
            } as Field
        }
        return {
            id: '',
            type: 'text',
            innerType: 'TEXT'
        } as Field
    }
    if (idVariable.type === VariableType.DEVICE_ID) {
        return {
            id: '',
            type: 'text',
            innerType: 'TEXT'
        } as Field
    }
    return undefined
}

type IdVariableToStringPayload = {
    formDsId?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo: AvailableFilterBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    idVariable: VariableADTvalue | undefined
}

const idVariableToString = (payload: IdVariableToStringPayload) => {
    const {
        formDsId,
        customViewData,
        dataSourceList,
        availableFieldBlocksInfo,
        pageVariableSchemas,
        pagePathParameter,
        pageQueryParameter,
        availableFilterBlocksInfo,
        idVariable
    } = payload
    if (!idVariable) {
        return
    }
    if (idVariable.type === VariableType.VALUE && idVariable.valueVariable?.value === '{currentUserId}') {
        return '当前登录用户'
    }
    if (idVariable.type === VariableType.PAGE_LINK && idVariable.pageLinkVariable?.value === 'CURRENT_PAGE') {
        return '当前页面链接'
    }
    if (idVariable.type === VariableType.DEVICE_ID && idVariable.deviceIdVariable?.value === DEVICE_ID_VALUE) {
        return '设备ID'
    }

    if (idVariable.type === VariableType.USER) {
        const dataSource = getDataSourceByIdVariable({
            customViewData,
            dataSourceList,
            idVariable,
            availableFieldBlocksInfo,
            pageVariableSchemas
        })
        const field = getFieldByIdVariable(dataSource, idVariable, availableFieldBlocksInfo, availableFilterBlocksInfo, pageVariableSchemas)
        if (dataSource && field) {
            return `${dataSource.name}/${field.name}`
        }
    }
    if (idVariable.type === VariableType.FORM) {
        const dataSource = getDataSourceByIdVariable({
            formDsId,
            dataSourceList,
            idVariable,
            availableFieldBlocksInfo
        })
        const field = getFieldByIdVariable(dataSource, idVariable, availableFieldBlocksInfo, availableFilterBlocksInfo, pageVariableSchemas)
        if (dataSource && field) {
            return `${dataSource.name}/${field.name}`
        }
    }
    if (idVariable.type === VariableType.IMAGE) {
        const { dataKey } = idVariable.imageVariable || {}
        if (!dataKey) {
            return
        }
        const imageName = imageVariableNameMap[dataKey]
        return `图片/${imageName}`
    }
    if (idVariable.type === VariableType.PAGE_DATA_QUERY) {
        const dataSource = getDataSourceByIdVariable({
            customViewData,
            dataSourceList,
            idVariable,
            availableFieldBlocksInfo,
            pageVariableSchemas
        })
        const field = getFieldByIdVariable(dataSource, idVariable, availableFieldBlocksInfo, availableFilterBlocksInfo, pageVariableSchemas)
        const schema = pageVariableSchemas?.find(item => item.id === idVariable.pageDataQueryVariable?.id)
        const variable = schema?.items.find(item => item.id === idVariable.pageDataQueryVariable?.itemId)
        if (dataSource && field) {
            return `${schema?.name || '-'}/${variable?.name || '-'}.${dataSource.name}.${field.name}`
        }
    }

    if (idVariable.type === VariableType.ROUTE_PATH) {
        const { routePathVariable } = idVariable
        const path = find(item => item.id === routePathVariable?.id, pagePathParameter || [])
        return `路径参数/${path?.name || '-'}`
    }

    if (idVariable.type === VariableType.ROUTE_QUERY) {
        const { routeQueryVariable } = idVariable
        const query = find(item => item.id === routeQueryVariable?.id, pageQueryParameter || [])
        return `查询参数/${query?.name || '-'}`
    }

    if (idVariable.type === VariableType.VIEW) {
        const dataSource = getDataSourceByIdVariable({
            customViewData,
            dataSourceList,
            idVariable,
            availableFieldBlocksInfo,
            pageVariableSchemas
        })
        const field = getFieldByIdVariable(
            dataSource,
            idVariable,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            pageVariableSchemas,
            customViewData?.extraFields
        )
        if (dataSource && field) {
            return `${dataSource.name}/${field.name}`
        }
    }

    if (idVariable.type === VariableType.INPUT) {
        const { blockId } = idVariable?.inputVariable ?? {}
        const fieldBlock = find(item => item.block.id === blockId, availableFieldBlocksInfo)
        if (fieldBlock) {
            return `输入框/${fieldBlock.block.title}`
        }
    }
    if (idVariable.type === VariableType.FILTER) {
        const { blockId, itemId } = idVariable?.filterVariable ?? {}
        const filterBlockInfo = find(item => item.block.id === blockId, availableFilterBlocksInfo)
        if (filterBlockInfo) {
            const filterItem = find(item => item.id === itemId, filterBlockInfo.block.config.filterItems)
            if (filterItem) {
                return `筛选控制器/${filterBlockInfo.block.title}/${filterItem.title}`
            }
        }
    }

    return undefined
}

export const paramToString: (
    field: Field,
    fieldValue: FieldCellValue,
    personOptions: UserOption[],
    roleOptions: RoleOption[],
    departmentOptions: DeptOption[]
) => string = (field, fieldValue, personOptions, roleOptions, departmentOptions) => {
    if (!fieldValue && typeof fieldValue !== 'boolean') {
        return ''
    }

    const { type } = field
    switch (type) {
        case 'id':
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'number':
        case 'notes': {
            return String(fieldValue)
        }
        case 'person': {
            if (!Array.isArray(fieldValue)) {
                return ''
            }
            return fieldValue
                .map(id => {
                    if (typeof id !== 'string') {
                        return ''
                    }
                    if (id === '{currentUserId}') {
                        return '当前用户'
                    }
                    const user = find(item => item.id === id, personOptions)
                    return user?.name || ''
                })
                .filter(Boolean)
                .join(',')
        }
        case 'role': {
            if (!Array.isArray(fieldValue)) {
                return ''
            }
            return fieldValue
                .map(id => {
                    if (typeof id !== 'string') {
                        return ''
                    }
                    const role = find(item => item.id === id, roleOptions)
                    return role?.name || ''
                })
                .filter(Boolean)
                .join(',')
        }
        case 'department': {
            if (!Array.isArray(fieldValue)) {
                return ''
            }
            return fieldValue
                .map(id => {
                    if (typeof id !== 'string') {
                        return ''
                    }
                    const department = find(item => item.id === id, departmentOptions)
                    return department?.name || ''
                })
                .filter(Boolean)
                .join(',')
        }
        case 'file':
        case 'video':
        case 'select': {
            if (!Array.isArray(fieldValue)) {
                return ''
            }
            return fieldValue.join(',')
        }

        case 'date': {
            // const secondFieldValue = data?.[1]?.valueVariable?.value
            // const secondDate = secondFieldValue && typeof secondFieldValue === 'number' ? lightFormat(secondFieldValue, 'yyyy-MM-dd') : ''
            // if (operator === 'between' && !secondDate) {
            //     return ''
            // }
            return typeof fieldValue === 'number' ? lightFormat(fieldValue, 'yyyy-MM-dd') : ''
        }
        case 'aggregation':
        case 'formula': {
            if (Array.isArray(fieldValue)) {
                return fieldValue.join(',')
            }
            return String(fieldValue)
        }

        case 'checkbox': {
            return fieldValue ? 'true' : 'false'
        }
        default: {
            return ''
        }
    }
}

type ParamListToStringPayload = {
    formDsId?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo: AvailableFilterBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    field: Field
    operator: string
    paramList: VariableADTvalue[]
    personOptions: UserOption[]
    roleOptions: RoleOption[]
    departmentOptions: DeptOption[]
}

const paramListToString: (payload: ParamListToStringPayload) => string = ({
    formDsId,
    customViewData,
    pageVariableSchemas,
    pagePathParameter,
    pageQueryParameter,
    dataSourceList,
    availableFieldBlocksInfo,
    availableFilterBlocksInfo,
    field,
    operator,
    paramList,
    personOptions,
    roleOptions,
    departmentOptions
}) => {
    const data = paramList
        .map(item => {
            if (item.type === VariableType.VALUE) {
                const variableValue = item.valueVariable?.value
                if (variableValue !== undefined) {
                    return paramToString(field, variableValue, personOptions, roleOptions, departmentOptions)
                }
            }
            if (item.type === VariableType.FIELD_ID) {
                return ''
            }
            if (item.type === VariableType.SYSTEM) {
                return item.systemVariable?.value ? variableSystemNameMap?.[item.systemVariable.value] || '' : ''
            }
            return idVariableToString({
                formDsId,
                customViewData,
                dataSourceList,
                availableFieldBlocksInfo,
                pageVariableSchemas,
                pagePathParameter,
                pageQueryParameter,
                availableFilterBlocksInfo,
                idVariable: item
            })
        })
        .filter(Boolean)

    if (operator === 'between' && data.length < 2) {
        return ''
    }
    return data.join(' ')
}

const isEmptyOperator = (operator: string) => {
    return operator === 'isEmpty' || operator === 'isNotEmpty'
}

const verifyParamsText = (operator: string, paramText: string) => {
    if (isEmptyOperator(operator)) {
        return true
    }
    return paramText
}

type ConditionToStringPayload = {
    formDsId?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    conditions: FilterConditions[]
    where: 'OR' | 'AND'
    personOptions: UserOption[]
    roleOptions: RoleOption[]
    departmentOptions: DeptOption[]
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo: AvailableFilterBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    level?: number
}

const conditionToString: (payload: ConditionToStringPayload) => string[] = ({
    formDsId,
    customViewData,
    dataSourceList,
    conditions,
    where,
    personOptions,
    roleOptions,
    departmentOptions,
    availableFieldBlocksInfo,
    pageVariableSchemas,
    pagePathParameter,
    pageQueryParameter,
    availableFilterBlocksInfo,
    level = 0
}) => {
    return conditions.reduce<string[]>((prev, cur) => {
        const childrenConditions = (cur as FilterGroupType)?.conditions
        const childrenWhere = (cur as FilterGroupType)?.where
        if (childrenConditions && childrenWhere) {
            const childrenText = conditionToString({
                formDsId,
                customViewData,
                dataSourceList,
                conditions: childrenConditions,
                where: childrenWhere,
                personOptions,
                roleOptions,
                departmentOptions,
                availableFieldBlocksInfo,
                pageVariableSchemas,
                pagePathParameter,
                pageQueryParameter,
                availableFilterBlocksInfo,
                level: level + 1
            })
            return [...prev, ...childrenText]
        }
        const condition = cur as FilterCommonCondition
        const { idVariable, operator, paramList } = condition
        const fieldText = idVariableToString({
            formDsId,
            customViewData,
            dataSourceList,
            availableFieldBlocksInfo,
            pageVariableSchemas,
            pagePathParameter,
            pageQueryParameter,
            availableFilterBlocksInfo,
            idVariable
        })
        const dataSource = getDataSourceByIdVariable({
            formDsId,
            customViewData,
            dataSourceList,
            idVariable,
            availableFieldBlocksInfo,
            pageVariableSchemas
        })
        const field = getFieldByIdVariable(
            dataSource,
            idVariable,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            pageVariableSchemas,
            customViewData?.extraFields
        )
        if (!fieldText || !field || !operator || !paramList) {
            return prev
        }
        const OperatorText = visibleFilterOperatorText[field?.innerType || 'NULL']?.[operator]
        const paramText = paramListToString({
            formDsId,
            customViewData,
            dataSourceList,
            availableFieldBlocksInfo,
            pageVariableSchemas,
            pagePathParameter,
            pageQueryParameter,
            availableFilterBlocksInfo,
            field,
            operator,
            paramList,
            personOptions,
            roleOptions,
            departmentOptions
        })
        if (!OperatorText || !verifyParamsText(operator, paramText)) {
            return prev
        }
        const realParamText = isEmptyOperator(operator) ? '' : paramText
        const whereText = where === 'AND' ? '且' : '或'
        const levelArr = Array.from({ length: level })
        const levelText = levelArr.map(item => '    ').join('')
        prev.push(`${levelText}${whereText} ${fieldText} ${OperatorText} ${realParamText}`)
        return prev
    }, [])
}

type FilterToStringPayload = {
    formDsId?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    filter: FilterFormType
    personOptions: UserOption[]
    roleOptions: RoleOption[]
    departmentOptions: DeptOption[]
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo: AvailableFilterBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
}

export const filterToString = (payload: FilterToStringPayload) => {
    const {
        formDsId,
        customViewData,
        dataSourceList,
        filter,
        personOptions,
        roleOptions,
        departmentOptions,
        availableFieldBlocksInfo,
        pageVariableSchemas,
        pagePathParameter,
        pageQueryParameter,
        availableFilterBlocksInfo
    } = payload
    const conditions = filter.expression?.conditions
    const where = filter.expression?.where
    if (!conditions || !where) {
        return
    }
    return conditionToString({
        formDsId,
        customViewData,
        dataSourceList,
        conditions,
        where,
        personOptions,
        roleOptions,
        departmentOptions,
        availableFieldBlocksInfo,
        pageVariableSchemas,
        pagePathParameter,
        pageQueryParameter,
        availableFilterBlocksInfo
    })
}

export const getFilterOfDeepBlockIds = (filter?: FilterFormType): Set<string> => {
    const blockIds = new Set<string>()
    if (!filter) {
        return blockIds
    }

    const conditions = clone(filter.expression?.conditions)
    const where = clone(filter.expression?.where)
    if (!conditions || !where) {
        return blockIds
    }

    const queue: FilterConditions[] = conditions
    while (queue.length > 0) {
        const condition = queue.shift()
        const childrenConditions = (condition as FilterGroupType)?.conditions
        const childrenWhere = (condition as FilterGroupType)?.where
        if (childrenConditions && childrenWhere) {
            for (const childrenCondition of childrenConditions) {
                queue.push(childrenCondition as FilterConditions)
            }
        }
        const filterCondition = condition as FilterCommonCondition
        const { idVariable, paramList } = filterCondition ?? {}
        if (idVariable && idVariable.type === VariableType.INPUT && idVariable.inputVariable?.blockId) {
            blockIds.add(idVariable.inputVariable.blockId)
        }
        if (paramList) {
            for (const param of paramList) {
                if (param.type === VariableType.INPUT && param.inputVariable?.blockId) {
                    blockIds.add(param.inputVariable.blockId)
                }
            }
        }
    }
    return blockIds
}

export const getFieldIdInFilter = (filter?: FilterFormType): Set<string> => {
    const fieldIds = new Set<string>()
    if (!filter) {
        return fieldIds
    }

    const conditions = clone(filter.expression?.conditions)
    const where = clone(filter.expression?.where)
    if (!conditions || !where) {
        return fieldIds
    }

    const queue: FilterConditions[] = conditions
    while (queue.length > 0) {
        const condition = queue.shift()
        const childrenConditions = (condition as FilterGroupType)?.conditions
        const childrenWhere = (condition as FilterGroupType)?.where
        if (childrenConditions && childrenWhere) {
            for (const childrenCondition of childrenConditions) {
                queue.push(childrenCondition as FilterConditions)
            }
        }
        const filterCondition = condition as FilterCommonCondition
        const { idVariable, paramList } = filterCondition ?? {}
        if (idVariable && idVariable.type === VariableType.FIELD_ID && idVariable.fieldIdVariable?.fieldId) {
            fieldIds.add(idVariable.fieldIdVariable.fieldId)
        }
    }
    return fieldIds
}

export type FilterItemIds = {
    blockId: string
    itemId: string
}

export const getFilterBlockItemIdsInFilter = (filter?: FilterFormType) => {
    const filterBlockItemIds: FilterItemIds[] = []
    if (!filter) {
        return { filterBlockItemIds }
    }

    const conditions = clone(filter.expression?.conditions)
    const where = clone(filter.expression?.where)
    if (!conditions || !where) {
        return { filterBlockItemIds }
    }

    const queue: FilterConditions[] = conditions
    while (queue.length > 0) {
        const condition = queue.shift()
        const childrenConditions = (condition as FilterGroupType)?.conditions
        const childrenWhere = (condition as FilterGroupType)?.where
        if (childrenConditions && childrenWhere) {
            for (const childrenCondition of childrenConditions) {
                queue.push(childrenCondition as FilterConditions)
            }
        }
        const filterCondition = condition as FilterCommonCondition
        const { idVariable, paramList } = filterCondition ?? {}
        if (idVariable && idVariable.type === VariableType.FILTER && idVariable.filterVariable?.itemId) {
            filterBlockItemIds.push({
                blockId: idVariable.filterVariable.blockId,
                itemId: idVariable.filterVariable.itemId
            })
        }
        if (paramList) {
            paramList.forEach(param => {
                if (param && param.type === VariableType.FILTER && param.filterVariable?.itemId) {
                    filterBlockItemIds.push({
                        blockId: param.filterVariable.blockId,
                        itemId: param.filterVariable.itemId
                    })
                }
            })
        }
    }
    return { filterBlockItemIds }
}
